import { ReactNode } from "react"

import { Icon, IconInfoCircle, IconProps } from "@tabler/icons-react"
import { type VariantProps, cva } from "class-variance-authority"

import { cn } from "@/lib/utils"

import { Tooltip } from "./Tooltip"

const tagVariants = cva(
  cn(
    "flex h-fit w-fit items-center justify-center gap-1 rounded-full px-3 py-1 transition-colors",
  ),
  {
    variants: {
      variant: {
        primary: "bg-tag-1 text-tag-1-foreground",
        secondary: "bg-tag-2 text-tag-2-foreground",
        "accent-1": "bg-tag-3 text-tag-3-foreground",
        "accent-2": "bg-tag-4 text-tag-4-foreground",
        "accent-3": "bg-tag-5 text-tag-5-foreground",
        success: "bg-tag-6 text-tag-6-foreground",
        error: "bg-tag-7 text-tag-7-foreground",
        neutral: "bg-tag-8 text-tag-8-foreground",
      },
      size: {
        small: "text-body2",
        medium: "text-body1",
      },
    },
    defaultVariants: {
      variant: "neutral",
      size: "small",
    },
  },
)

export type TagProps = Omit<React.ComponentPropsWithRef<"span">, "color" | "children"> &
  VariantProps<typeof tagVariants> & {
    label: ReactNode
    icon?: Icon
    iconPosition?: "left" | "right"
    iconStroke?: IconProps["stroke"]
    iconClassName?: string
    tooltipMsg?: string
  }

export function Tag({
  className = "",
  label,
  icon,
  iconPosition = "left",
  iconStroke = 3,
  iconClassName,
  size = "small",
  variant = "neutral",
  tooltipMsg,
  ...props
}: TagProps) {
  const IconComponent = icon

  const iconSize = size === "small" ? 12 : size === "medium" ? 14 : 16

  return (
    <span
      className={cn(
        tagVariants({
          variant,
          size,
          className,
        }),
      )}
      {...props}
    >
      {iconPosition === "left" && IconComponent ? (
        <IconComponent className={iconClassName} stroke={iconStroke} size={iconSize} />
      ) : null}
      <span className="overflow-hidden">{label}</span>
      {iconPosition === "right" && IconComponent ? (
        <IconComponent className={iconClassName} stroke={iconStroke} size={iconSize} />
      ) : null}
      {tooltipMsg && (
        <Tooltip content={tooltipMsg} className="max-w-xs">
          <IconInfoCircle
            className={cn(iconClassName, "ml-0.5 print:hidden")}
            stroke={2}
            size={iconSize}
          />
        </Tooltip>
      )}
    </span>
  )
}

export { tagVariants }
